import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import sidebar from '../../utils/sidebar';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import DefaultButton from '../../../../components/Buttons/Default';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline, IoTrashOutline } from 'react-icons/io5';
import { BsTrash3 } from 'react-icons/bs';
import { TfiFilter } from 'react-icons/tfi';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { del, get } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../../../context/settings';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import FiltersDrawer from './Drawers/Filters';
import { printBillStatus } from '../../utils/helpers';
import ViewDrawer from './Drawers/View';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/purchases/bills/edit/${data.id}`, { state: { billData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setBillToDelete(data.id);
        }}
        size="sm"
        outline
        color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function BillsPage() {
  const { t } = useTranslation();
  const { formatDate } = useSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['bill_number', 'status', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(Boolean).length;

  const getBills = useCallback(async () => {
    setLoading(true);
    let query = '/purchases/bills?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.bills?.data) {
      setRowData(data.bills.data);
      setTotalPages(data.bills.last_page);
      setTotalRecords(data.bills.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getBills();
  }, [getBills]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const deleteBill = async () => {
    const data = await del('/purchases/bills/delete/' + billToDelete);
    if (data.success) {
      toast.success(data.message || t('purchases.billDeleted'));
      getBills();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setBillToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'bill_number', headerName: t('purchases.billNumber'), minWidth: 140, flex: 1, sortable: true },
    {
      headerName: t('purchases.supplier'),
      minWidth: 200,
      flex: 1,
      sortable: true,
      valueGetter: (p) => {
        const s = p.data.supplier;
        if (!s) return '-';
        return [s.firstname, s.lastname].filter(Boolean).join(' ') || s.company_name || '-';
      }
    },
    { field: 'due_date', headerName: t('sales.dueDate'), minWidth: 130, sortable: true, valueFormatter: (p) => formatDate(p.value) },
    {
      field: 'status',
      headerName: t('common.status'),
      minWidth: 140,
      sortable: true,
      cellRenderer: (params) => {
        return printBillStatus(params.value);
      }
    },
    {
      field: 'total_amount', 
      headerName: t('common.amount'), 
      minWidth: 120, 
      sortable: true,
      valueFormatter: (p) => {
        return p.value + ' ' + p.data.currency?.toUpperCase();
      }
    },
    {
      headerName: t('common.actions'),
      minWidth: 100,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    },
  ], [t, formatDate]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setBillToDelete }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    setSelectedBillId(event.data.id);
    setDrawerOpen(true);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t("purchases.bills")} onReload={getBills} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t("common.filter")}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton
            color="primary"
            size="sm"
            onClick={() => navigate('/purchases/bills/create')}
          >
            {t("purchases.newBill")}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={billToDelete !== null}
        title={t("purchases.deleteBill")}
        message={t("purchases.deleteBillConfirm")}
        onConfirm={deleteBill}
        toggle={() => setBillToDelete(null)}
      />

      <ViewDrawer 
        billId={selectedBillId}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onDelete={() => {
          setDrawerOpen(false);
          setBillToDelete(selectedBillId);
        }}
        refreshBills={getBills}
      />
    </SidebarLayout>
  );
}

export default BillsPage;
